import {
  cryptoUint32,
  randomBelow,
  type Uint32Source,
} from "@/lib/tool-kit/random-fun/rng";

export type Side = "heads" | "tails";

export interface Streak {
  side: Side | null;
  length: number;
}

/** Everything the running tally shows. */
export interface CoinStats {
  heads: number;
  tails: number;
  current: Streak;
  longest: Streak;
}

export const EMPTY_STATS: CoinStats = {
  heads: 0,
  tails: 0,
  current: { side: null, length: 0 },
  longest: { side: null, length: 0 },
};

/** One fair flip: a single unbiased bit from the shared generator. */
export function flipOnce(source: Uint32Source = cryptoUint32): Side {
  return randomBelow(2, source) === 0 ? "heads" : "tails";
}

export function flipMany(
  count: number,
  source: Uint32Source = cryptoUint32
): Side[] {
  const results: Side[] = [];
  for (let i = 0; i < count; i += 1) {
    results.push(flipOnce(source));
  }
  return results;
}

/**
 * Adds flips, in order, to a tally. It is a pure function of the previous tally, so it
 * is meant for a functional state update (`setStats((prev) => recordFlips(prev, …))`):
 * two results that land close together — a batch during the single-flip animation — both
 * build on the latest tally instead of one overwriting the other from a stale copy.
 */
export function recordFlips(
  stats: CoinStats,
  results: readonly Side[]
): CoinStats {
  let { heads, tails } = stats;
  let current = { ...stats.current };
  let longest = { ...stats.longest };
  for (const side of results) {
    if (side === "heads") {
      heads += 1;
    } else {
      tails += 1;
    }
    current =
      current.side === side
        ? { side, length: current.length + 1 }
        : { side, length: 1 };
    if (current.length > longest.length) {
      longest = { ...current };
    }
  }
  return { heads, tails, current, longest };
}
